import React, { useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Alert, Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { ACCENT, SURFACE } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import TutorsPlayClues from "./TutorsPlayClues";
import TutorsPlayGrid from "./TutorsPlayGrid";
import TutorsPlayScramble from "./TutorsPlayScramble";
import TutorsWorkspaceShell from "./TutorsWorkspaceShell";
import { buildTutorCrossword } from "./tutorCrosswordFromText";
import { normalizeGuess, placementKey } from "./tutorCrosswordPlay";
import { readTutorLibrary } from "./tutorHubDetails";
import { fillTutorPuzzleClues, saveTutorStoryClues } from "./tutorStoryClues";
import useTutorHub from "./useTutorHub";

export default function TutorsPlayPage() {
    const { t } = usePagesTranslation();
    const { id } = useParams();
    const { details, loading, error, patchDetails } = useTutorHub();
    const detailsRef = useRef(details);
    const patchRef = useRef(patchDetails);
    detailsRef.current = details;
    patchRef.current = patchDetails;
    const library = useMemo(() => readTutorLibrary(details), [details]);
    const story = library.find((item) => item.id === id) || null;
    const basePuzzle = useMemo(
        () => (story?.sourceText ? buildTutorCrossword(story.sourceText) : null),
        [story?.sourceText],
    );
    const [puzzle, setPuzzle] = useState(null);
    const [selectedKey, setSelectedKey] = useState("");
    const [foundKeys, setFoundKeys] = useState(() => new Set());
    const [drafts, setDrafts] = useState({});
    const [cluesOpen, setCluesOpen] = useState(true);
    const [wrongKey, setWrongKey] = useState("");
    const [cluesLoading, setCluesLoading] = useState(false);
    const clueSignature = (story?.clues || [])
        .map((row) => `${row.word}:${row.definition}`)
        .join("|");

    useEffect(() => {
        setFoundKeys(new Set());
        setDrafts({});
        const first = basePuzzle?.placements?.[0];
        setSelectedKey(first ? placementKey(first) : "");
        setPuzzle(basePuzzle);
    }, [basePuzzle]);

    useEffect(() => {
        if (!story || !basePuzzle?.grid?.length) {
            return undefined;
        }
        let cancelled = false;
        const run = async () => {
            setCluesLoading(true);
            try {
                const result = await fillTutorPuzzleClues({
                    puzzle: basePuzzle,
                    savedClues: story.clues,
                    title: story.title,
                    sourceText: story.sourceText,
                    storyId: story.id,
                });
                if (cancelled) {
                    return;
                }
                setPuzzle(result.puzzle);
                if (result.changed) {
                    await saveTutorStoryClues(
                        detailsRef.current,
                        story.id,
                        result.clues,
                        patchRef.current,
                    );
                }
            } finally {
                if (!cancelled) {
                    setCluesLoading(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [basePuzzle, clueSignature, story]);

    const placements = puzzle?.placements || [];
    const selected = placements.find((placement) => placementKey(placement) === selectedKey) || null;
    const total = placements.length;
    const solved = foundKeys.size > 0 && foundKeys.size === total;

    const selectPlacement = (placement) => {
        if (!placement) {
            setSelectedKey("");
            return;
        }
        setSelectedKey(placementKey(placement));
        setWrongKey("");
    };

    const checkPlacement = (placement, rawValue) => {
        if (!placement) {
            return;
        }
        const key = placementKey(placement);
        if (foundKeys.has(key)) {
            return;
        }
        const guess = normalizeGuess(rawValue != null ? rawValue : drafts[key]);
        if (guess && guess === String(placement.word || "").toUpperCase()) {
            setFoundKeys((prev) => {
                const next = new Set(prev);
                next.add(key);
                return next;
            });
            setDrafts((prev) => ({ ...prev, [key]: String(placement.word || "").toUpperCase() }));
            setWrongKey("");
            const nextFound = new Set(foundKeys);
            nextFound.add(key);
            const next = placements.find((item) => !nextFound.has(placementKey(item)));
            setSelectedKey(next ? placementKey(next) : "");
            return;
        }
        setWrongKey(key);
    };

    const clearSelection = () => {
        if (selected && !foundKeys.has(selectedKey)) {
            setDrafts((prev) => ({ ...prev, [selectedKey]: "" }));
        }
        setWrongKey("");
    };

    const backButton = (
        <Tooltip title={t("tutors.play-back", "Back to story library")}>
            <IconButton
                component={RouterLink}
                to="/tutors/library"
                aria-label={t("tutors.play-back", "Back to story library")}
                sx={{ minWidth: 44, minHeight: 44, color: ACCENT.light }}
            >
                <ArrowBackIcon />
            </IconButton>
        </Tooltip>
    );

    if (!loading && !story) {
        return (
            <TutorsWorkspaceShell title={t("tutors.play-title", "Play")} loading={loading} error={error} leading={backButton}>
                <Alert severity="info">
                    {t("tutors.play-missing", "This story is not available.")}{" "}
                    <Button component={RouterLink} to="/tutors/library" size="small" sx={{ textTransform: "none" }}>
                        {t("nav.tutors-library", "Story library")}
                    </Button>
                </Alert>
            </TutorsWorkspaceShell>
        );
    }

    return (
        <TutorsWorkspaceShell
            title={story?.title || t("tutors.play-title", "Play")}
            loading={loading}
            error={error}
            maxWidth={1200}
            leading={backButton}
        >
            {basePuzzle?.error === "need-words" ? (
                <Alert severity="warning">
                    {t(
                        "tutors.worksheets-need-words",
                        "Need at least four puzzle words (4–12 letters). Add WORD — meaning lines, or a longer story with more content words.",
                    )}
                </Alert>
            ) : null}

            {puzzle?.grid?.length ? (
                <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title={t("tutors.play-clear", "Clear the current selection")}>
                            <IconButton
                                aria-label={t("tutors.play-clear", "Clear the current selection")}
                                onClick={clearSelection}
                                sx={{ minWidth: 44, minHeight: 44, color: ACCENT.light }}
                            >
                                <RestartAltIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("tutors.play-clues", "Word Clues")}>
                            <IconButton
                                aria-label={t("tutors.play-clues", "Word Clues")}
                                onClick={() => setCluesOpen((open) => !open)}
                                sx={{
                                    minWidth: 44,
                                    minHeight: 44,
                                    color: cluesOpen ? "#fff" : ACCENT.light,
                                    bgcolor: cluesOpen ? ACCENT.main : "transparent",
                                }}
                            >
                                <MenuBookIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {cluesLoading ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t("tutors.play-clues-loading", "Getting clues for words that do not have one yet.")}
                        </Alert>
                    ) : null}
                    {solved ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {t("tutors.play-complete", "Every word is filled in.")}
                        </Alert>
                    ) : null}
                    {wrongKey && selectedKey === wrongKey ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {t("tutors.play-wrong", "That word does not match. Try again.")}
                        </Alert>
                    ) : null}

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: cluesOpen ? 6 : 12 }}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${SURFACE.border}`,
                                    bgcolor: SURFACE.sunken,
                                    overflowX: "auto",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 1,
                                        bgcolor: "#fff",
                                        overflowX: "auto",
                                    }}
                                >
                                <TutorsPlayGrid
                                    puzzle={puzzle}
                                    selectedKey={selectedKey}
                                    foundKeys={foundKeys}
                                    drafts={drafts}
                                    onSelect={selectPlacement}
                                    onType={(letter) => {
                                        if (!selected || foundKeys.has(selectedKey)) {
                                            return;
                                        }
                                        const next = normalizeGuess(`${drafts[selectedKey] || ""}${letter}`)
                                            .slice(0, String(selected.word || "").length);
                                        setDrafts((prev) => ({ ...prev, [selectedKey]: next }));
                                        setWrongKey("");
                                        if (next === String(selected.word || "").toUpperCase()) {
                                            checkPlacement(selected, next);
                                        }
                                    }}
                                    onBackspace={() => {
                                        if (!selected || foundKeys.has(selectedKey)) {
                                            return;
                                        }
                                        setDrafts((prev) => ({
                                            ...prev,
                                            [selectedKey]: (prev[selectedKey] || "").slice(0, -1),
                                        }));
                                        setWrongKey("");
                                    }}
                                    onCheck={() => checkPlacement(selected)}
                                />
                                </Box>
                                <TutorsPlayScramble
                                    wordKey={selected ? selectedKey : ""}
                                    word={selected?.word || ""}
                                    draft={selected ? drafts[selectedKey] || "" : ""}
                                    found={selected ? foundKeys.has(selectedKey) : false}
                                    onDraftChange={(next) => {
                                        if (!selected || foundKeys.has(selectedKey)) {
                                            return;
                                        }
                                        const guess = normalizeGuess(next).slice(0, String(selected.word || "").length);
                                        setDrafts((prev) => ({ ...prev, [selectedKey]: guess }));
                                        setWrongKey("");
                                        if (guess === String(selected.word || "").toUpperCase()) {
                                            checkPlacement(selected, guess);
                                        }
                                    }}
                                />
                            </Box>
                        </Grid>
                        {cluesOpen ? (
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TutorsPlayClues
                                    puzzle={puzzle}
                                    selectedKey={selectedKey}
                                    foundKeys={foundKeys}
                                    onSelect={selectPlacement}
                                    onSubmitGuess={checkPlacement}
                                    onHide={() => setCluesOpen(false)}
                                />
                            </Grid>
                        ) : null}
                    </Grid>
                </>
            ) : null}
        </TutorsWorkspaceShell>
    );
}
